"use client";

import * as React from "react";
import { parseDate, type CalendarDate } from "@internationalized/date";
import { format, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";

interface CalendarDatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
}

function CalendarDatePicker({
  value,
  onChange,
  placeholder,
  className,
}: CalendarDatePickerProps) {
  const tc = useTranslations("common");
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    try {
      return parseDate(value);
    } catch {
      return undefined;
    }
  }, [value]);

  const displayText = React.useMemo(() => {
    if (!value) return null;
    try {
      return format(parseISO(value), "PPP");
    } catch {
      return value;
    }
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "inline-flex h-8 w-full items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-sm font-normal transition-colors hover:bg-muted dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
          !value && "text-muted-foreground",
          className
        )}
      >
        <CalendarIcon className="h-4 w-4 shrink-0" />
        {displayText ?? (placeholder || tc("pickDate"))}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 rounded-lg bg-popover shadow-md ring-1 ring-foreground/10">
          <Calendar
            value={dateValue}
            onChange={(date: CalendarDate) => {
              onChange(date.toString());
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

export { CalendarDatePicker };
